import React, { useMemo, useState } from 'react';
import Modal from '../Modal';
import { licencesApi, errorOf, TYPE_LABEL, TYPE_HINT, PERIOD_LABEL, amount } from '../../lib/licences';
import './Licences.css';

/**
 * Add a licence, or change one: the product and vendor, the type and seats,
 * what it costs and how often, the dates, who looks after it and the company
 * it is for, the reference and notes, and whether every joiner gets it.
 *
 * The server checks everything again and says what is wrong in a sentence
 * (lib/licences.js clean), which is shown above the buttons; this only keeps
 * the obvious from being sent. `options` is GET /licences/options - the
 * people and companies to choose from.
 */
const blank = {
  product: '', vendor: '', licence_type: 'per_user', seats: '', cost: '', currency: 'EUR', billing_period: 'annual',
  start_date: '', renewal_date: '', end_date: '', auto_renew: false, owner_id: '', company_id: '',
  reference: '', notes: '', standard: false
};

const fromLicence = (l) => ({
  ...blank,
  ...Object.fromEntries(Object.keys(blank).map((k) => [k, l[k] === null || l[k] === undefined ? blank[k] : l[k]])),
  seats: l.seats === null || l.seats === undefined ? '' : String(l.seats),
  cost: l.cost === null || l.cost === undefined ? '' : String(l.cost),
  billing_period: l.billing_period || (l.cost === null ? 'annual' : '')
});

const PER_YEAR = { monthly: 12, quarterly: 4, annual: 1, biennial: 0.5, triennial: 1 / 3, one_off: 0 };

export default function LicenceForm({ licence = null, options, onClose, onSaved }) {
  const [form, setForm] = useState(() => (licence ? fromLicence(licence) : blank));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const yearly = useMemo(() => {
    const c = Number(form.cost);
    if (form.cost === '' || !Number.isFinite(c) || !form.billing_period) return null;
    return c * (PER_YEAR[form.billing_period] ?? 0);
  }, [form.cost, form.billing_period]);

  const people = (options?.people || []);
  const companies = (options?.companies || []).filter((c) => !c.archived_at || c.id === form.company_id);

  const save = async (e) => {
    e.preventDefault();
    if (!form.product.trim()) { setProblem('Say what the product is.'); return; }
    setBusy(true);
    setProblem('');
    const body = {
      ...form,
      seats: form.seats === '' ? null : Number(form.seats),
      cost: form.cost === '' ? null : form.cost,
      billing_period: form.billing_period || null,
      owner_id: form.owner_id || null,
      company_id: form.company_id || null,
      start_date: form.start_date || null,
      renewal_date: form.renewal_date || null,
      end_date: form.end_date || null
    };
    try {
      const saved = licence ? await licencesApi.update(licence.id, body) : await licencesApi.create(body);
      onSaved(saved);
    } catch (err) {
      setProblem(errorOf(err, 'The licence could not be saved.'));
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="btn btn-plain" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="submit" form="lic-form" className="btn btn-primary" disabled={busy}>
        {busy ? 'Saving…' : licence ? 'Save changes' : 'Add the licence'}
      </button>
    </>
  );

  return (
    <Modal title={licence ? `Change ${licence.product}` : 'Add a licence'}
      subtitle={licence ? null : 'A licence or subscription the firm pays for. Seats are given out once it is added.'}
      onClose={onClose} footer={footer} wide>
      <form id="lic-form" className="lic-form" onSubmit={save} noValidate data-testid="licence-form">
        <div className="lic-field">
          <label htmlFor="lic-product">Product</label>
          <input id="lic-product" value={form.product} onChange={set('product')} maxLength={160} required
            placeholder="Microsoft 365 Business Standard" autoComplete="off" />
        </div>
        <div className="lic-field">
          <label htmlFor="lic-vendor">Vendor</label>
          <input id="lic-vendor" value={form.vendor} onChange={set('vendor')} maxLength={120} placeholder="Microsoft"
            list="lic-vendors" autoComplete="off" />
          {(options?.vendors || []).length > 0 && (
            <datalist id="lic-vendors">{options.vendors.map((v) => <option key={v} value={v} />)}</datalist>
          )}
        </div>
        <div className="lic-field">
          <label htmlFor="lic-type">Type</label>
          <select id="lic-type" value={form.licence_type} onChange={set('licence_type')}>
            {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="lic-hint">{TYPE_HINT[form.licence_type]}</p>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-seats">Seats bought</label>
          <input id="lic-seats" type="number" inputMode="numeric" min="0" step="1" value={form.seats} onChange={set('seats')}
            placeholder={form.licence_type === 'site' ? 'No limit' : 'e.g. 25'} />
          <p className="lic-hint">Leave empty for no limit.</p>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-cost">Cost</label>
          <div className="lic-cost">
            <input id="lic-cost" type="number" inputMode="decimal" min="0" step="0.01" value={form.cost} onChange={set('cost')}
              placeholder="0.00" />
            <input aria-label="Currency" value={form.currency} onChange={(e) => set('currency')(e.target.value.toUpperCase().slice(0, 3))}
              maxLength={3} />
          </div>
          {yearly !== null && form.billing_period !== 'one_off' && (
            <p className="lic-hint" data-testid="lic-yearly">{`${amount(yearly, form.currency || 'EUR')} a year`}</p>
          )}
        </div>
        <div className="lic-field">
          <label htmlFor="lic-period">Billed</label>
          <select id="lic-period" value={form.billing_period || ''} onChange={set('billing_period')}>
            <option value="">Not set</option>
            {Object.entries(PERIOD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-start">Started</label>
          <input id="lic-start" type="date" value={form.start_date} onChange={set('start_date')} />
        </div>
        <div className="lic-field">
          <label htmlFor="lic-renewal">Renews</label>
          <input id="lic-renewal" type="date" value={form.renewal_date} onChange={set('renewal_date')} />
          <p className="lic-hint">Reminders go out 90, 30 and 7 days before.</p>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-end">Ends</label>
          <input id="lic-end" type="date" value={form.end_date} onChange={set('end_date')} />
          <p className="lic-hint">A contract&rsquo;s last day, if it has one. It is marked expired after it.</p>
        </div>
        <div className="lic-field">
          <label className="lic-check">
            <input type="checkbox" checked={form.auto_renew} onChange={set('auto_renew')} />
            <span>Renews by itself
              <span className="lic-hint" style={{ display: 'block' }}>When the date passes, the next renewal is set one billing period on.</span>
            </span>
          </label>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-owner">Looked after by</label>
          <select id="lic-owner" value={form.owner_id || ''} onChange={set('owner_id')}>
            <option value="">Nobody in particular</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <p className="lic-hint">A ticket raised by an automation rule is raised for them.</p>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-company">For company</label>
          <select id="lic-company" value={form.company_id || ''} onChange={set('company_id')}>
            <option value="">The firm (no company)</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="lic-field">
          <label htmlFor="lic-ref">Reference</label>
          <input id="lic-ref" value={form.reference} onChange={set('reference')} maxLength={120}
            placeholder="Contract or account number" autoComplete="off" />
          <p className="lic-hint">Never a licence key or a password.</p>
        </div>
        <div className="lic-field">
          <label className="lic-check">
            <input type="checkbox" checked={form.standard} onChange={set('standard')} />
            <span>Every joiner gets it
              <span className="lic-hint" style={{ display: 'block' }}>Offered on the joiner checklist, to give in one go.</span>
            </span>
          </label>
        </div>
        <div className="lic-field lic-field-wide">
          <label htmlFor="lic-notes">Notes</label>
          <textarea id="lic-notes" rows={3} value={form.notes} onChange={set('notes')} maxLength={4000} />
        </div>
        {problem && <p className="as-error lic-form-error" role="alert">{problem}</p>}
      </form>
    </Modal>
  );
}
